import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Database, FileText, Globe, HardDrive, Trash2 } from 'lucide-react';
import { Badge, Button, Input, Card, Alert, Loader, Select, Tabs, ProgressBar } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

const POLL_INTERVAL_MS = 2000;
const BLANK_KB = { name: '', description: '', application_id: null };
const DEFAULT_MAX_PAGES = 40;
const HARD_MAX_PAGES = 100;

// Client-side mirror of services/kb/crawl/urlUtils.js's detectDocTypeFromUrl — an
// instant hint only; the server re-detects on POST and the worker verifies against
// the real Content-Type.
const DOC_TYPE_LABELS = {
  pdf: 'PDF document',
  docx: 'Word document',
  pptx: 'PowerPoint presentation',
  xlsx: 'Excel workbook',
  csv: 'CSV file',
  txt: 'text file',
  md: 'Markdown file',
};

function withProtocol(raw) {
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

function detectDocType(rawUrl) {
  try {
    const pathname = new URL(withProtocol(rawUrl)).pathname.toLowerCase();
    const dot = pathname.lastIndexOf('.');
    if (dot === -1) return null;
    return DOC_TYPE_LABELS[pathname.slice(dot + 1)] ? pathname.slice(dot + 1) : null;
  } catch {
    return null;
  }
}

function StatusBadge({ status }) {
  const variant = { ready: 'success', pending: 'secondary', processing: 'warning', error: 'error' };
  return <Badge variant={variant[status] || 'secondary'} size="sm">{status}</Badge>;
}

/** form → CRUD `data` payload, including the per-KB semantic settings (null'd out
 *  when the toggle is off so a disabled KB never carries a stale provider/model). */
function kbFormToData(form) {
  const on = !!form.semantic_extraction;
  return {
    name: form.name,
    description: form.description,
    semantic_extraction: on,
    // semantic_connection_id pins the exact AI-model Connection to use (disambiguates
    // when a company has several for one provider). semantic_provider is kept in sync
    // as the fallback when no connection is chosen / it's later deleted (server resolves
    // by provider then, per services/chat/providers.js).
    semantic_provider: on ? (form.semantic_provider || 'openai') : null,
    semantic_connection_id: on ? (form.semantic_connection_id || null) : null,
    semantic_model: on ? (form.semantic_model || null) : null,
    application_id: form.application_id || null,
  };
}

function KbMetaSection({ form, setField, apps }) {
  return (
    <Card className="p-3 space-y-3">
      <Input label="Name" value={form.name || ''} onChange={(e) => setField('name', e.target.value)} />
      <Input label="Description" value={form.description || ''} onChange={(e) => setField('description', e.target.value)} />
      {apps.length > 0 && (
        <Select
          label="Application (scope to one app, or leave empty for company-wide)"
          options={[{ label: 'Company-wide (all apps)', value: '' }, ...apps.map((a) => ({ label: a.name, value: a.id }))]}
          value={form.application_id != null ? form.application_id : ''}
          onChange={(v) => setField('application_id', v || null)}
          allowClear
          searchable
        />
      )}
    </Card>
  );
}

// Static fallback used until the live provider list loads (or if it can't be fetched
// — e.g. the user lacks agents.view). Mirrors the AI Model providers in
// services/chat/providers.js's registry — the value must be one of those keys (that's
// what turns the choice into a live model). At runtime KnowledgeBaseDetail replaces
// this with the /api/agent/providers list, annotated by which providers the company
// actually has an AI-model Connection (or server env key) for.
const SEMANTIC_PROVIDER_OPTIONS = [
  { label: 'OpenAI', value: 'openai' },
  { label: 'Claude (Anthropic)', value: 'anthropic' },
  { label: 'Gemini (Google)', value: 'gemini' },
  { label: 'Grok (xAI)', value: 'grok' },
  { label: 'OpenRouter', value: 'openrouter' },
  { label: 'OpenCode Go', value: 'opencode-go' },
];

/**
 * Build the semantic-provider <Select> options from the live provider registry
 * (/api/agent/providers → key/label/env-configured) annotated with the company's
 * active AI-model Connections. A provider is "available" — no warning suffix — when the
 * company has a connection for it OR the server has an env key, matching how
 * services/chat/providers.js resolveApiKey actually resolves a key at ingestion time.
 */
function buildProviderOptions(providers, connectionRows) {
  if (!Array.isArray(providers) || !providers.length) return SEMANTIC_PROVIDER_OPTIONS;
  const connected = new Set((connectionRows || []).map((c) => c.provider));
  return providers.map((p) => ({
    value: p.key,
    label: (connected.has(p.key) || p.configured) ? p.label : `${p.label} — no connection or key`,
  }));
}

/**
 * Per-KB semantic knowledge-graph settings (schema/sync.json: app_knowledge_bases).
 * The toggle opts this KB into graphify's LLM extraction pass; provider/model pick
 * which model runs it. The API key is NOT entered here — it resolves from the
 * company's matching "AI Model" Connection (or the server env key), so keys stay in
 * one encrypted place (services/chat/providers.js resolveApiKey).
 */
function KbSemanticSection({
  form, setField, aiConnections, providerOptions,
}) {
  const on = !!form.semantic_extraction;
  const hasConnections = aiConnections.length > 0;
  const connectionOptions = aiConnections.map((c) => ({ label: `${c.name} (${c.provider})`, value: String(c.id) }));

  // Picking a connection stores its id (the exact key to use) AND its provider (keeps
  // semantic_provider in sync for the model default + the deleted-connection fallback).
  const pickConnection = (value) => {
    const conn = aiConnections.find((c) => String(c.id) === value);
    setField('semantic_connection_id', conn ? conn.id : null);
    if (conn) setField('semantic_provider', conn.provider);
  };

  return (
    <Card className="p-3 space-y-3">
      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" className="mt-1" checked={on} onChange={(e) => setField('semantic_extraction', e.target.checked)} />
        <span>
          <span className="block font-medium text-sm">Build a semantic knowledge graph</span>
          <span className="block text-xs text-secondary-500">
            Runs an AI model over each ingested document to link concepts across sources, so search can follow
            relationships — not just text similarity. Applies to sources ingested or re-ingested after this is turned on.
          </span>
        </span>
      </label>
      {on && (
        <div className="grid grid-cols-2 gap-3 pl-6">
          <div className="space-y-1">
            <label className="text-xs text-secondary-500" htmlFor={hasConnections ? 'kb-sem-connection' : 'kb-sem-provider'}>
              {hasConnections ? 'AI Model connection' : 'Provider'}
            </label>
            {hasConnections ? (
              <Select
                id="kb-sem-connection"
                options={connectionOptions}
                value={form.semantic_connection_id != null && form.semantic_connection_id !== '' ? String(form.semantic_connection_id) : ''}
                onChange={pickConnection}
                placeholder="Select an AI Model connection"
              />
            ) : (
              <Select
                id="kb-sem-provider"
                options={providerOptions}
                value={form.semantic_provider || 'openai'}
                onChange={(value) => setField('semantic_provider', value)}
                placeholder="Provider"
              />
            )}
          </div>
          <Input
            label="Model (optional)"
            value={form.semantic_model || ''}
            onChange={(e) => setField('semantic_model', e.target.value)}
            placeholder="default for the provider"
          />
        </div>
      )}
      {on && (
        <p className="text-xs text-secondary-500 pl-6">
          {hasConnections
            ? 'Pick which AI Model connection runs the extraction — this is how you choose among multiple keys for the same provider. No key is entered here.'
            : "No AI Model connections yet — it falls back to this provider's server key. Add one under Connections to pick a specific key."}
        </p>
      )}
    </Card>
  );
}

const MAX_THEMES_SHOWN = 20;

/** The theme list: collapsed rows with passage counts, summary on expand. */
function ThemesList({ themes, openHash, setOpenHash }) {
  if (!themes.length) {
    return <p className="text-sm text-secondary-500">No themes yet — they are generated from the graph after ingestion.</p>;
  }
  return (
    <>
      <ul className="space-y-1">
        {themes.slice(0, MAX_THEMES_SHOWN).map((t) => (
          <ThemeRow key={t.hash} theme={t} open={openHash === t.hash} onToggle={() => setOpenHash((h) => (h === t.hash ? null : t.hash))} />
        ))}
      </ul>
      {themes.length > MAX_THEMES_SHOWN && (
        <p className="text-xs text-secondary-500">…and {themes.length - MAX_THEMES_SHOWN} more.</p>
      )}
    </>
  );
}

/** One expandable theme row: label + passage-count badge, summary on expand. */
function ThemeRow({ theme, open, onToggle }) {
  return (
    <li className="border-t border-[var(--app-border)] pt-1">
      <button onClick={onToggle} className="flex w-full items-center gap-2 text-left" aria-label={open ? 'Collapse theme' : 'Expand theme'}>
        <span className="p-0.5 text-secondary-500">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
        <span className="truncate text-sm font-medium">{theme.label}</span>
        <span className="ml-auto shrink-0"><Badge variant="secondary" size="sm">{theme.memberCount} passages</Badge></span>
      </button>
      {open && theme.summary && <p className="py-1 pl-6 pr-2 text-sm text-secondary-500">{theme.summary}</p>}
    </li>
  );
}

/** Node counts + the chunks-linked health badge for one loaded graph. */
function GraphStatsLine({ graph, gap }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-secondary-500">
      <span>{graph.nodes} nodes · {graph.chunkNodes} chunks · {(graph.communities || []).length} themes</span>
      {(graph.chunksTotal || 0) > 0 && (
        gap > 0
          ? <Badge variant="warning" size="sm">{graph.chunksInGraph}/{graph.chunksTotal} chunks linked</Badge>
          : <Badge variant="success" size="sm">All {graph.chunksTotal} chunks linked</Badge>
      )}
    </div>
  );
}

/** Read-only roll-up of the KB's knowledge graph + per-community theme summaries
 *  (GET /api/knowledge/bases/:id/graph), with an on-demand regeneration action
 *  (POST .../summaries/refresh). The graph itself builds during ingestion and
 *  theme summaries generate in the ingest worker — this panel only surfaces what
 *  exists (and recovers what a fail-open run skipped), following SourcePagesRow's
 *  fetch-once idiom rather than the sources poll loop. The global-mode hint line
 *  is deliberate: kb_search's themes-over-chunks mode is otherwise undiscoverable
 *  from the UI. */
function GraphThemesSection({ knowledgeBaseId, hasSources }) {
  const { fetchApi } = useAppApi();
  const [graph, setGraph] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [openHash, setOpenHash] = useState(null);

  const load = () => fetchApi('knowledgeGraph', knowledgeBaseId)
    .then((res) => { setGraph(res || null); setErr(''); })
    .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message));

  useEffect(() => { load(); }, [knowledgeBaseId]);

  const rebuild = async () => {
    setBusy(true);
    setNote('');
    try {
      const res = await fetchApi('knowledgeSummariesRefresh', knowledgeBaseId);
      const parts = [];
      if (res.summarized) parts.push(`${res.summarized} generated`);
      if (res.skipped) parts.push(`${res.skipped} skipped`);
      if (res.removed) parts.push(`${res.removed} removed`);
      setNote(parts.length ? `Refresh complete — ${parts.join(', ')}.` : 'Refresh complete — nothing changed.');
      await load();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const themes = (graph && graph.communities) || [];
  const gap = graph ? (graph.chunksTotal || 0) - (graph.chunksInGraph || 0) : 0;
  let rebuildLabel = 'Generate themes';
  if (busy) rebuildLabel = 'Rebuilding…';
  else if (themes.length) rebuildLabel = 'Rebuild themes';

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Knowledge graph &amp; themes</h2>
      <Card className="p-3 space-y-3">
        {err && <p className="text-xs text-red-600">{err}</p>}
        {!err && !graph && <p className="text-xs text-secondary-500">Loading graph…</p>}
        {graph && !graph.hasGraph && (
          <p className="text-sm text-secondary-500">
            {hasSources
              ? 'No knowledge graph yet — it builds automatically as sources finish ingesting.'
              : 'The knowledge graph builds as sources are ingested — add a source below to get started.'}
          </p>
        )}
        {graph && !!graph.hasGraph && (
          <>
            <GraphStatsLine graph={graph} gap={gap} />
            {gap > 0 && (
              <p className="text-xs text-secondary-500">
                Some chunks are not in the graph yet — this closes automatically on the next ingestion.
              </p>
            )}
            <ThemesList themes={themes} openHash={openHash} setOpenHash={setOpenHash} />
            <p className="text-xs text-secondary-500">
              Agents can search these themes with <span className="font-medium">kb_search</span> in global mode.
            </p>
          </>
        )}
        <div className="flex items-center gap-2">
          <Can module="knowledge" action="author">
            <Button size="sm" variant="outline" onClick={rebuild} disabled={busy || !graph || !graph.hasGraph}>
              {rebuildLabel}
            </Button>
          </Can>
          {busy && <span className="text-xs text-secondary-500">This can take a minute on large graphs…</span>}
          {!busy && note && <span className="text-xs text-secondary-500">{note}</span>}
        </div>
      </Card>
    </div>
  );
}

/**
 * URL tab — one input that routes itself: the live hint (and the server's `detected`
 * echo after submit) tells the user whether their URL will be extracted as a single
 * document or crawled as a website, and only the website case exposes a page budget.
 */
function AddUrlSourceForm({ knowledgeBaseId, onAdded }) {
  const { fetchApi } = useAppApi();
  const [url, setUrl] = useState('');
  const [maxPages, setMaxPages] = useState(DEFAULT_MAX_PAGES);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const trimmed = url.trim();
  const docType = trimmed ? detectDocType(trimmed) : null;
  const isWebsite = Boolean(trimmed) && !docType;

  const submit = async (e) => {
    e.preventDefault();
    if (!trimmed) return;
    setBusy(true);
    setErr('');
    setConfirmation('');
    try {
      const res = await fetchApi('knowledgeSourceUrl', {
        knowledgeBaseId,
        url: withProtocol(trimmed),
        maxPages: isWebsite ? Number(maxPages) || DEFAULT_MAX_PAGES : undefined,
      });
      setConfirmation(res?.detected === 'document'
        ? `Queued as a ${DOC_TYPE_LABELS[res.docType] || 'document'} — it will be extracted directly.`
        : `Queued as a website — internal pages and linked documents will be crawled (up to ${Number(maxPages) || DEFAULT_MAX_PAGES} pages).`);
      setUrl('');
      onAdded();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      <p className="text-xs text-secondary-500">
        Paste a link to a document (PDF, DOCX, PPTX, XLSX, CSV, TXT/MD) to extract it directly, or any web page to crawl the site's internal pages.
      </p>
      <div className="flex items-center gap-2">
        <Input placeholder="https://example.com/docs or https://example.com/guide.pdf" value={url} onChange={(e) => setUrl(e.target.value)} className="flex-1" />
        <Button size="sm" type="submit" disabled={busy || !trimmed}>{busy ? 'Adding…' : 'Add URL'}</Button>
      </div>
      {trimmed && (
        <p className="text-xs text-secondary-500">
          {docType
            ? <>Detected: <span className="font-medium">{DOC_TYPE_LABELS[docType]}</span> — will be extracted directly.</>
            : <>Detected: <span className="font-medium">website</span> — internal pages and linked documents will be crawled.</>}
        </p>
      )}
      {isWebsite && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-secondary-500" htmlFor="kb-max-pages">Max pages</label>
          <Input
            id="kb-max-pages" type="number" min={1} max={HARD_MAX_PAGES} value={maxPages}
            onChange={(e) => setMaxPages(e.target.value)} className="w-24"
          />
          <span className="text-xs text-secondary-500">1–{HARD_MAX_PAGES}; each page is chunked and embedded, so bigger crawls take longer.</span>
        </div>
      )}
      {confirmation && <p className="text-xs text-emerald-700">{confirmation}</p>}
      {err && <p className="text-xs text-red-600">{err}</p>}
    </form>
  );
}

function AddFileSourceForm({ knowledgeBaseId, onAdded }) {
  const { fetchApi } = useAppApi();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileInputRef = useRef(null);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setErr('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('knowledgeBaseId', knowledgeBaseId);
      await fetchApi('knowledgeSourceFile', formData);
      onAdded();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-secondary-500">Upload a document from your computer (max 15 MB).</p>
      <div className="flex items-center gap-2">
        <input ref={fileInputRef} type="file" accept=".pdf,.docx,.pptx,.xlsx,.csv,.txt,.md" className="hidden" onChange={onFileChange} />
        <Button size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={busy}>
          {busy ? 'Uploading…' : 'Upload file'}
        </Button>
        <span className="text-xs text-secondary-500">PDF, DOCX, PPTX, XLSX, CSV, TXT/MD</span>
        {err && <span className="text-xs text-red-600">{err}</span>}
      </div>
    </div>
  );
}

/** SQLite database upload — the file lands in private Spaces storage, its schema
 * (+ up to 10 sample rows per table) is ingested like any other source, and the
 * agent's korm tooling (kb_db_query/kb_db_describe) can read it live. */
function AddSqliteSourceForm({ knowledgeBaseId, onAdded }) {
  const { fetchApi } = useAppApi();
  const [includeTables, setIncludeTables] = useState('');
  const [excludeTables, setExcludeTables] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [err, setErr] = useState('');
  const fileInputRef = useRef(null);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setErr('');
    setConfirmation('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('knowledgeBaseId', knowledgeBaseId);
      if (includeTables.trim()) formData.append('includeTables', JSON.stringify(csvList(includeTables)));
      if (excludeTables.trim()) formData.append('excludeTables', JSON.stringify(csvList(excludeTables)));
      // 202: the file is staged and queued — the upload to Spaces, table parsing and
      // ingestion all happen in the background, so the row appears as "pending" in the
      // sources list (which already polls until it resolves).
      const res = await fetchApi('knowledgeSourceSqlite', formData);
      setConfirmation(`Queued "${res.filename || file.name}" — its tables are parsed in the background; status updates below.`);
      setIncludeTables('');
      setExcludeTables('');
      onAdded();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-secondary-500">
        Upload a SQLite database file (max {SQLITE_MAX_MB} MB). It is stored privately, its tables/columns/relationships and sample rows are ingested as knowledge, and the agent can query the data exactly via korm tooling.
      </p>
      <div className="flex items-center gap-2">
        <input ref={fileInputRef} type="file" accept={SQLITE_ACCEPT} className="hidden" onChange={onFileChange} />
        <Button size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={busy}>
          {busy ? 'Uploading…' : 'Upload SQLite file'}
        </Button>
        <span className="text-xs text-secondary-500">.sqlite, .sqlite3, .db</span>
        {err && <span className="text-xs text-red-600">{err}</span>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Include tables (blank = all)" value={includeTables} onChange={(e) => setIncludeTables(e.target.value)} />
        <Input placeholder="Exclude tables (optional)" value={excludeTables} onChange={(e) => setExcludeTables(e.target.value)} />
      </div>
      <p className="text-xs text-secondary-500">
        By default every table is ingested (up to 20). Use Include to pick only certain tables, or Exclude to skip some — comma-separated.
      </p>
      {confirmation && <p className="text-xs text-emerald-700">{confirmation}</p>}
    </div>
  );
}

const DB_ENGINE_OPTIONS = [
  { label: 'MySQL', value: 'mysql' },
  { label: 'Postgres', value: 'postgres' },
];

/** SQLite upload limit — mirrors the server's SQLITE_MAX_UPLOAD_MB default (256). */
const SQLITE_MAX_MB = 256;
const SQLITE_ACCEPT = '.sqlite,.sqlite3,.db';

const csvList = (s) => s.split(',').map((t) => t.trim()).filter(Boolean);
const connLabel = (c) => (c.status && c.status !== 'ok' ? `${c.name} (${c.status})` : c.name);

/** Human-readable result of POST /knowledge/sources/db (tableCount/totalTables/truncated). */
function dbSourceConfirmation(res) {
  if (!res) return 'Queued for ingestion.';
  if (res.truncated) return `Queued ${res.tableCount} of ${res.totalTables} tables (capped) — use Include to pick specific tables.`;
  const n = res.tableCount ?? 0;
  return `Queued ${n} table${n === 1 ? '' : 's'} for ingestion.`;
}

function AddDbSourceForm({ knowledgeBaseId, onAdded }) {
  const { fetchApi } = useAppApi();
  const [connections, setConnections] = useState([]);
  const [selected, setSelected] = useState('manual'); // a connection id (string) or 'manual'
  const [form, setForm] = useState({ engine: 'mysql', host: '', port: 3306, database: '', user: '', password: '' });
  const [includeTables, setIncludeTables] = useState('');
  const [excludeTables, setExcludeTables] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Saved database Connections (reads go through generic CRUD; secrets are stripped
  // by the model). If the user can't list connections, we just fall back to manual.
  useEffect(() => {
    fetchApi('crud', {
      model: 'AppConnection', action: 'list',
      where: { category_key: 'database', is_active: true },
      select: ['id', 'name', 'provider', 'status'],
    })
      .then((res) => {
        const rows = Array.isArray(res) ? res : (res?.data ?? []);
        setConnections(rows);
        if (rows.length) setSelected(String(rows[0].id));
      })
      .catch(() => {});
  }, []);

  const isManual = selected === 'manual';

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setConfirmation('');
    const body = { knowledgeBaseId, includeTables: csvList(includeTables), excludeTables: csvList(excludeTables) };
    if (isManual) {
      if (!form.host || !form.database || !form.user) {
        setErr('Host, database, and user are required');
        return;
      }
      body.connection = { engine: form.engine, host: form.host, port: Number(form.port) || 3306, database: form.database, user: form.user, password: form.password };
    } else {
      body.connectionId = Number(selected);
    }
    setBusy(true);
    try {
      const res = await fetchApi('knowledgeSourceDb', body);
      setConfirmation(dbSourceConfirmation(res));
      setIncludeTables('');
      setExcludeTables('');
      if (isManual) setForm({ engine: 'mysql', host: '', port: 3306, database: '', user: '', password: '' });
      onAdded();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const connectionOptions = [
    ...connections.map((c) => ({ label: connLabel(c), value: String(c.id) })),
    { label: 'Enter manually…', value: 'manual' },
  ];

  return (
    <form onSubmit={submit} className="space-y-2">
      <p className="text-xs text-secondary-500">
        Ingest table schemas and sample rows from a SQL database. Pick a saved database Connection (no need to re-enter credentials) or enter details manually — credentials are encrypted at rest.
      </p>

      <div className="space-y-1">
        <label className="text-xs text-secondary-500" htmlFor="kb-db-connection">Connection</label>
        <Select
          id="kb-db-connection"
          options={connectionOptions}
          value={selected}
          onChange={setSelected}
          placeholder={connections.length ? 'Select a connection' : 'No saved database connections — enter manually'}
        />
      </div>

      {isManual && (
        <div className="grid grid-cols-2 gap-2">
          <Select options={DB_ENGINE_OPTIONS} value={form.engine} onChange={(value) => setField('engine', value)} placeholder="Engine" />
          <Input placeholder="Host" value={form.host} onChange={(e) => setField('host', e.target.value)} />
          <Input placeholder="Port" type="number" value={form.port} onChange={(e) => setField('port', e.target.value)} />
          <Input placeholder="Database" value={form.database} onChange={(e) => setField('database', e.target.value)} />
          <Input placeholder="User" value={form.user} onChange={(e) => setField('user', e.target.value)} />
          <Input placeholder="Password" type="password" value={form.password} onChange={(e) => setField('password', e.target.value)} />
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Include tables (blank = all)" value={includeTables} onChange={(e) => setIncludeTables(e.target.value)} />
        <Input placeholder="Exclude tables (optional)" value={excludeTables} onChange={(e) => setExcludeTables(e.target.value)} />
      </div>
      <p className="text-xs text-secondary-500">
        By default every table is ingested (up to 20). Use Include to pick only certain tables, or Exclude to skip some — comma-separated.
      </p>

      <div className="flex items-center gap-2">
        <Button size="sm" type="submit" disabled={busy}>{busy ? 'Adding…' : 'Add database source'}</Button>
        {confirmation && <span className="text-xs text-emerald-700">{confirmation}</span>}
        {err && <span className="text-xs text-red-600">{err}</span>}
      </div>
    </form>
  );
}

function sourceLabel(source) {
  if (source.source_type === 'url') return source.source_url;
  if (source.source_type === 'file' || source.source_type === 'sqlite_file') return source.filename;
  return `DB source (${source.chunk_count || 0} chunks)`;
}

/** Lazily-loaded per-page breakdown of a crawled url source (GET /sources/:id/pages). */
function SourcePagesRow({ sourceId }) {
  const { fetchApi } = useAppApi();
  const [pages, setPages] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetchApi('knowledgeSourcePages', sourceId)
      .then((res) => setPages(res.pages || []))
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message));
  }, [sourceId]);

  return (
    <tr className="border-t border-[var(--app-border)] bg-[var(--app-border)]/10">
      <td colSpan={6} className="px-4 py-2">
        {err && <p className="text-xs text-red-600">{err}</p>}
        {!err && !pages && <p className="text-xs text-secondary-500">Loading pages…</p>}
        {pages && !pages.length && <p className="text-xs text-secondary-500">No page breakdown available.</p>}
        {pages && pages.length > 0 && (
          <ul className="space-y-1">
            {pages.map((page) => (
              <li key={page.url || 'single'} className="flex items-center gap-2 text-xs">
                <span className="truncate max-w-md" title={page.url || ''}>{pageRoute(page.url)}</span>
                <span className="text-secondary-500 truncate max-w-xs">{page.title || ''}</span>
                <span className="text-secondary-500 ml-auto shrink-0">{page.chunkCount} chunks</span>
              </li>
            ))}
          </ul>
        )}
      </td>
    </tr>
  );
}

function pageRoute(url) {
  if (!url) return '(source)';
  try {
    return new URL(url).pathname || '/';
  } catch {
    return url;
  }
}

function SourceRow({ source, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const label = sourceLabel(source);
  const crawling = source.status === 'processing' && (source.pages_found || 0) > 0;
  const expandable = source.source_type === 'url' && (source.chunk_count || 0) > 0;

  return (
    <>
      <tr className="border-t border-[var(--app-border)]">
        <td className="px-4 py-2 text-xs uppercase text-secondary-500">{source.source_type}</td>
        <td className="px-4 py-2 truncate max-w-xs">
          <span className="flex items-center gap-1">
            {expandable && (
              <button onClick={() => setExpanded((v) => !v)} className="p-0.5 text-secondary-500 hover:text-secondary-700" aria-label={expanded ? 'Collapse pages' : 'Expand pages'}>
                {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            )}
            <span className="truncate">{label}</span>
          </span>
        </td>
        <td className="px-4 py-2">
          <div className="space-y-1">
            <StatusBadge status={source.status} />
            {crawling && (
              <div className="flex items-center gap-2">
                <div className="w-24"><ProgressBar value={source.pages_done || 0} max={source.pages_found || 1} size="xs" ariaLabel="Crawl progress" /></div>
                <span className="text-xs text-secondary-500">{source.pages_done || 0}/{source.pages_found}</span>
              </div>
            )}
            {source.status === 'ready' && (source.pages_failed || 0) > 0 && (
              <span className="text-xs text-amber-700">{source.pages_failed} page{source.pages_failed === 1 ? '' : 's'} failed</span>
            )}
          </div>
        </td>
        <td className="px-4 py-2">{source.chunk_count || 0}</td>
        <td className="px-4 py-2 text-red-600 text-xs">{source.error || ''}</td>
        <td className="px-4 py-2 text-right">
          <button onClick={() => onDelete(source.id)} className="p-1 text-red-500 hover:bg-red-50 rounded" aria-label="Delete source">
            <Trash2 size={14} />
          </button>
        </td>
      </tr>
      {expanded && <SourcePagesRow sourceId={source.id} />}
    </>
  );
}

function SourcesSection({ knowledgeBaseId, sources, onAdded, onDelete }) {
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Sources</h2>
      <Can module="knowledge" action="author">
        <Card className="p-3">
          <Tabs defaultIndex={0} orientation="horizontal" variant="line" size="sm">
            <Tabs.List>
              <Tabs.Tab index={0} iconLeft={<Globe size={14} />}>Website or URL</Tabs.Tab>
              <Tabs.Tab index={1} iconLeft={<FileText size={14} />}>File upload</Tabs.Tab>
              <Tabs.Tab index={2} iconLeft={<Database size={14} />}>Database</Tabs.Tab>
              <Tabs.Tab index={3} iconLeft={<HardDrive size={14} />}>SQLite database</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panels className="pt-3">
              <Tabs.Panel index={0}><AddUrlSourceForm knowledgeBaseId={knowledgeBaseId} onAdded={onAdded} /></Tabs.Panel>
              <Tabs.Panel index={1}><AddFileSourceForm knowledgeBaseId={knowledgeBaseId} onAdded={onAdded} /></Tabs.Panel>
              <Tabs.Panel index={2}><AddDbSourceForm knowledgeBaseId={knowledgeBaseId} onAdded={onAdded} /></Tabs.Panel>
              <Tabs.Panel index={3}><AddSqliteSourceForm knowledgeBaseId={knowledgeBaseId} onAdded={onAdded} /></Tabs.Panel>
            </Tabs.Panels>
          </Tabs>
        </Card>
      </Can>
      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-[var(--app-border)]/30 text-left text-xs uppercase text-secondary-500">
            <tr>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Source</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Chunks</th>
              <th className="px-4 py-2">Error</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => <SourceRow key={s.id} source={s} onDelete={onDelete} />)}
            {sources.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-secondary-500">No sources yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/**
 * Knowledge Base detail/edit/create page (`/app-knowledge-base/:id`, or `/new`) —
 * mirrors DynamicToolDetail.jsx's isNew/save() split for the KB's own name/
 * description, plus a sources section (only once the KB actually exists — a source
 * needs a real knowledgeBaseId to attach to). Polls the sources list while any source
 * is pending/processing, same idiom ChatPanel.jsx uses for turn status.
 */
export default function KnowledgeBaseDetail() {
  const { id } = useParams();
  const isNew = id === 'new';
  const nav = useNavigate();
  const { fetchApi } = useAppApi();

  const [form, setForm] = useState(isNew ? { ...BLANK_KB } : null);
  const [sources, setSources] = useState([]);
  const [aiConnections, setAiConnections] = useState([]);
  const [providerOptions, setProviderOptions] = useState(SEMANTIC_PROVIDER_OPTIONS);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const pollTimerRef = useRef(null);

  // Load the company's active AI-model Connections so the semantic-graph section can
  // offer them by name (pick the exact key to use). The provider registry is fetched
  // too, only as the fallback provider-key list shown when there are no connections.
  // Not gated on isNew — the semantic section shows on the create page too.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchApi('agentProviders').catch(() => null),
      fetchApi('crud', {
        model: 'AppConnection', action: 'list',
        where: { category_key: 'ai-model', is_active: true }, select: ['id', 'name', 'provider'],
      }).catch(() => null),
      fetchApi('applicationsList').catch(() => []),
    ]).then(([provRes, connRes, appsRes]) => {
      if (cancelled) return;
      const connRows = Array.isArray(connRes) ? connRes : (connRes?.data ?? []);
      setAiConnections(connRows);
      setProviderOptions(buildProviderOptions(provRes?.providers, connRows));
      setApps(Array.isArray(appsRes) ? appsRes : (appsRes?.data ?? []));
    });
    return () => { cancelled = true; };
  }, []);

  const refreshSources = () => {
    if (isNew) return;
    fetchApi('knowledgeBaseSources', id)
      .then((res) => setSources(res.sources || []))
      .catch(() => {});
  };

  useEffect(() => {
    if (isNew) return undefined;
    fetchApi('crud', { model: 'AppKnowledgeBase', action: 'show', where: { id } })
      .then((res) => setForm(res && res.data ? res.data : res))
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
    refreshSources();
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, [id]);

  useEffect(() => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    const hasPending = sources.some((s) => s.status === 'pending' || s.status === 'processing');
    if (hasPending) pollTimerRef.current = setInterval(refreshSources, POLL_INTERVAL_MS);
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, [sources]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    setErr('');
    const data = kbFormToData(form);
    try {
      if (isNew) {
        const created = await fetchApi('crud', { model: 'AppKnowledgeBase', action: 'create', data });
        // The korm-js CRUD contract's `create` response has been observed in three
        // shapes across this app's own pages: {data: {...}}, a bare row, and — the one
        // that actually comes back here — an array-like object keyed "0" ({"0": {id,
        // ...}}), which `created.id` alone (DynamicToolDetail.jsx's identical, older
        // pattern) silently resolves to undefined for, producing a broken
        // /app-knowledge-base/undefined URL. Falls through all three rather than
        // assuming one.
        const newId = created?.data?.id ?? created?.[0]?.id ?? created?.id;
        if (!newId) throw new Error('Created the knowledge base, but could not read its new id back from the response.');
        nav(`/app-knowledge-base/${newId}`, { replace: true });
      } else {
        await fetchApi('crud', { model: 'AppKnowledgeBase', action: 'update', where: { id }, data });
      }
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteSource = async (sourceId) => {
    if (!window.confirm('Delete this source and its ingested chunks?')) return;
    try {
      await fetchApi('knowledgeSourceDelete', sourceId);
      refreshSources();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    }
  };

  if (loading) return <PageShell><Loader text="Loading knowledge base…" /></PageShell>;
  if (err && !form) return <PageShell><Alert variant="error" title="Could not load">{err}</Alert></PageShell>;
  if (!form) return null;

  return (
    <PageShell width="narrow" className="space-y-3">
      <PageHeader
        backTo="/app-knowledge-base"
        backLabel="Knowledge Bases"
        title={isNew ? 'New Knowledge Base' : form.name}
        actions={(
          <Can module="knowledge" action="author">
            <Button size="sm" onClick={save} disabled={saving || !form.name}>{saving ? 'Saving…' : 'Save'}</Button>
          </Can>
        )}
      />

      {err && <Alert variant="error" title="Error">{err}</Alert>}

      <KbMetaSection form={form} setField={setField} apps={apps} />
      <KbSemanticSection form={form} setField={setField} aiConnections={aiConnections} providerOptions={providerOptions} />
      {!isNew && <GraphThemesSection knowledgeBaseId={id} hasSources={sources.length > 0} />}
      {!isNew && <SourcesSection knowledgeBaseId={id} sources={sources} onAdded={refreshSources} onDelete={deleteSource} />}
    </PageShell>
  );
}
